import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { PlOption } from 'pl-core-services';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  startWith,
} from 'rxjs';

@Component({
  selector: 'pl-dropdown',
  templateUrl: 'pl-dropdown.component.html',
  styleUrls: ['pl-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PlDropdownComponent,
      multi: true,
    },
  ],
})
export class PlDropdownComponent
  implements ControlValueAccessor, AfterViewInit
{
  @Input() label = '';

  @Input() disabled = false;

  @Input() set options(newOptions: PlOption[]) {
    this.options$.next(newOptions ?? []);
    // Options (e.g. from an async lookup) can arrive after writeValue() ran
    // with nothing to match yet - re-resolve against the raw pending value
    // now that a match may exist.
    this.resolvePendingValue();
  }

  get options() {
    return this.options$.value;
  }

  @Output() changed: EventEmitter<string> = new EventEmitter();

  // MatAutocompleteTrigger fixed this same class of bug upstream
  // (angular/components#26761 / PR #27422) for the case where the native
  // input is cleared by typing - it calls option.deselect(false) on the
  // previously-selected option. That fix lives in `_handleInput`, which
  // only runs off the native `input` DOM event, so it never fires for our
  // own clear button or for `writeValue` (e.g. patching an existing
  // value in edit mode). Mirror the same emitEvent:false pattern here for
  // those paths - `false` is required so it doesn't re-emit
  // onSelectionChange, which MatAutocompleteTrigger would otherwise merge
  // into its own panel-closing stream.
  @ViewChild('plDropdownAutocomplete') private autocomplete?: MatAutocomplete;

  private options$ = new BehaviorSubject<PlOption[]>([]);

  filteredOptions$: Observable<PlOption[]>;

  searchCtrl = new UntypedFormControl('');

  selected: PlOption = null;

  // The raw value handed to writeValue(), kept independently of `selected`
  // so it can be re-resolved once options that weren't available yet show up.
  private pendingValue: string = null;

  get value() {
    return this.selected?.value;
  }

  set value(val: string) {
    this.pendingValue = val;
    if (this.resolvePendingValue()) {
      this.onChange?.(this.value);
      this.changed.emit(this.value);
    }
  }

  onTouched?: Function;

  onChange?: Function;

  constructor(private destroyRef: DestroyRef) {
    const search$ = this.searchCtrl.valueChanges.pipe(
      startWith(this.searchCtrl.value)
    );

    this.filteredOptions$ = combineLatest([this.options$, search$]).pipe(
      map(([options, search]) => this.applyFilter(options ?? [], search))
    );
  }

  ngAfterViewInit(): void {
    // Options rendered as MatOptions can appear after we've already
    // resolved `selected` (e.g. once the *ngFor for filteredOptions$
    // catches up) - re-sync their visual selected state when that happens.
    this.autocomplete?.options?.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncOptionSelection());
    // MatAutocomplete's content children resolve before this hook runs, so
    // an initial `selected` set via writeValue() before the view existed
    // needs one immediate sync - `.changes` only fires on later updates.
    this.syncOptionSelection();
  }

  optionSelected(ev: MatAutocompleteSelectedEvent) {
    this.value = ev.option.value;
  }

  clear() {
    this.value = null;
  }

  writeValue(obj: any): void {
    // Model-driven, per the ControlValueAccessor contract - must not echo
    // back through onChange/changed, which are reserved for user-driven
    // selection (optionSelected/clear).
    this.pendingValue = obj;
    this.resolvePendingValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchCtrl.disable();
    } else {
      this.searchCtrl.enable();
    }
  }

  setSearchValue() {
    this.searchCtrl.setValue(this.selected?.label ?? '');
  }

  onBlur(): void {
    this.setSearchValue();
    this.onTouched?.(this.value);
  }

  /** Resolves `pendingValue` against the current options, silently -
   * callers that need to notify the model do so themselves. Returns
   * whether `selected` changed. */
  private resolvePendingValue(): boolean {
    const newOption =
      this.options.find((opt) => opt.value === this.pendingValue) ?? null;
    // A new `options` array can hand back a different object for the same
    // value (e.g. a relabeled location) - refresh `selected` for that too,
    // but only report a value change to callers deciding whether to notify.
    const valueChanged = newOption?.value !== this.selected?.value;
    const optionChanged = newOption !== this.selected;

    if (optionChanged) {
      this.selected = newOption;
      this.syncOptionSelection();
    }

    this.setSearchValue();
    return valueChanged;
  }

  private syncOptionSelection(): void {
    this.autocomplete?.options?.forEach((opt) => {
      if (opt.value === this.selected?.value) {
        opt.select(false);
      } else {
        opt.deselect(false);
      }
    });
  }

  private applyFilter(options: PlOption[], search: string): PlOption[] {
    const filterValue = search.toLowerCase();

    return options.filter((opt) =>
      opt.label.toLowerCase().includes(filterValue)
    );
  }
}
